import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RequestCategory } from '../../../../models/request-category.model';
import { RequestCategoryService } from '../../../../services/request-category.service';
import { MessageService } from '../../../../../global/services/message/message.abstract.service';
import { ConfirmDialogV2Component } from '../../../../../global/components/dialogs/confirm-dialog-v2/confirm-dialog-v2.component';
import { InformationalDialogV2Component } from 'src/app/global/components/dialogs/informational-dialog-v2/informational-dialog-v2.component';
import { ErrorSeverity } from 'src/app/global/components/dialogs/informational-dialog/informational-dialog.component';

@Component({
    selector: 'app-category-list',
    templateUrl: './category-list.component.html',
    styleUrls: ['./category-list.component.scss'],
})
export class CategoryListComponent implements OnInit, OnDestroy {
    @Output() edit = new EventEmitter<RequestCategory>();

    categories: RequestCategory[] = [];

    loading = false;

    private destroy$ = new Subject<void>();

    constructor(
        private categoryService: RequestCategoryService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.loadCategories();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadCategories(): void {
        this.loading = true;
        this.categoryService
            .getRequestCategories()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (categories) => {
                    this.categories = categories || [];
                    this.loading = false;
                },
                error: (error) => {
                    this.loading = false;
                    this.showErrorMessage('request_v2.category_list.load_error');
                },
            });
    }

    onEdit(category: RequestCategory): void {
        this.edit.emit(category);
    }

    onDelete(category: RequestCategory): void {
        const dialogRef = this.dialog.open(ConfirmDialogV2Component, {
            width: '450px',
            data: {
                title: this.translate.instant('request_v2.category_list.delete_title'),
                body: this.translate.instant('request_v2.category_list.confirm_delete'),
                buttons: {
                    confirm: {
                        text: this.translate.instant('common.delete'),
                        class: 'bciti-button bciti-button--red',
                    },
                    cancel: {
                        text: this.translate.instant('common.cancel'),
                        class: 'bciti-button bciti-button--blue-inversed',
                    },
                },
            },
        });

        dialogRef.componentInstance.onSubmit.subscribe((result: boolean) => {
            if (result) {
                dialogRef.componentInstance.loading = true;
                this.categoryService
                    .deleteRequestCategory(category._id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            dialogRef.close();
                            this.loadCategories();
                            this.showSuccessMessage('request_v2.category_list.delete_success');
                        },
                        error: (error) => {
                            dialogRef.componentInstance.loading = false;
                            this.showErrorDialog(error?.error?.messages?.[0]);
                        },
                    });
            } else {
                dialogRef.close();
            }
        });
    }

    private showSuccessMessage(key: string): void {
        this.messageService.success(this.translate.instant(key));
    }

    private showErrorMessage(key: string): void {
        this.messageService.error(this.translate.instant(key));
    }

    private showErrorDialog(errorMessage: string): void {
        this.dialog.open(InformationalDialogV2Component, {
            autoFocus: false,
            panelClass: 'informational-dialog',
            data: {
                body: !!errorMessage ? errorMessage : this.translate.instant('common.errors.retry'),
                errorSeverity: ErrorSeverity.Error,
                buttonText: this.translate.instant('common.retry'),
            },
        });
    }

    getCategoryName(category: RequestCategory): string {
        if (typeof category.name === 'object' && category.name !== null) {
            const currentLang = this.translate.currentLang || 'en';
            return category.name[currentLang] || category.name.en || '';
        }
        return category.name as string;
    }

    getSubCategoryName(subCategory: any): string {
        if (typeof subCategory.name === 'object' && subCategory.name !== null) {
            const currentLang = this.translate.currentLang || 'en';
            return subCategory.name[currentLang] || subCategory.name.en || '';
        }
        return subCategory.name as string;
    }

    getManagerInitials(manager: { firstName: string; lastName: string }): string {
        if (!manager || (!manager.firstName && !manager.lastName)) {
            return '';
        }
        const firstInitial = manager.firstName ? manager.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = manager.lastName ? manager.lastName.charAt(0).toUpperCase() : '';
        return `${firstInitial}${lastInitial}`;
    }

    getManagerFullName(manager: { firstName: string; lastName: string }): string {
        if (!manager) {
            return '';
        }
        const firstName = manager.firstName || '';
        const lastName = manager.lastName || '';
        return `${firstName} ${lastName}`.trim();
    }

    getChipBackgroundColor(color: string, opacity = 0.15): string {
        if (!color) return 'rgba(0, 0, 0, 0.05)'; // Default light gray if no color

        const hex = color.replace('#', '');
        if (hex.length !== 3 && hex.length !== 6) {
            return 'rgba(0, 0, 0, 0.05)'; // Fallback for invalid hex
        }

        let r, g, b;
        if (hex.length === 3) {
            r = parseInt(hex.substring(0, 1) + hex.substring(0, 1), 16);
            g = parseInt(hex.substring(1, 2) + hex.substring(1, 2), 16);
            b = parseInt(hex.substring(2, 3) + hex.substring(2, 3), 16);
        } else {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        }

        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
}
